import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../css/UnlockDCR.css";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import PobDatewiseDetails from "./PobdatewiseDetails";
import formatDate from "../utils/formatDate";
import Loader from "../dist/Loader";
import CustomButton from "./CustomButton";
import { formatTimeTo12Hour } from "../utils/formatTime";
import { hasAccess } from "../utils/access";

const LeaveReportPage = () => {
  const [states, setStates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [monthYearOptions, setMonthYearOptions] = useState([]);
  const [selectedMonthYear, setSelectedMonthYear] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const isExpenseUser = hasAccess("dashboard_expense");
  const [leaveData, setLeaveData] = useState([]);
  const [editDate, setEditDate] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const isCoordinator = hasAccess("dashboard_coordinator");
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const navigate = useNavigate();
  const location = useLocation();

  const normalizeMonthYear = (my) => {
    if (!my) return my;
    return my
      .replace(/\bSept\.?\b/, "Sep")
      .replace(/\s+/g, " ")
      .trim();
  };



  const isRowLocked = (item) => {
    const dateStr = item.date.slice(0, 10); // ✅ local date, no timezone shift
    return dateStr === todayStr && item.leave_type_name === "Morning Punch";
  };

  useEffect(() => {
    // ✅ FIX: Only restore cached leaveData if filters match what was saved.
    // This prevents stale data from a previous employee showing up when
    // the user comes back from a sub-page without changing filters.
    if (location.state?.fromLeaveReport && location.state.filters) {
      const {
        filters,
        leaveData: savedLeaveData,
        selectedEditDate,
        editFormData: savedEditForm,
      } = location.state;

      const restoredState = filters.selectedState || "";
      const restoredEmployee = filters.selectedEmployee || "";
      const restoredMonthYear = filters.selectedMonthYear || "";

      setSelectedState(restoredState);
      setSelectedEmployee(restoredEmployee);
      setSelectedMonthYear(restoredMonthYear);

      if (savedLeaveData) setLeaveData(savedLeaveData);
      if (selectedEditDate) setEditDate(selectedEditDate);
      if (savedEditForm) setEditFormData(savedEditForm);

      // ✅ FIX: Clear location state immediately after restoring so it never
      // re-injects old cached data on subsequent renders or filter changes.
      navigate(location.pathname, { replace: true, state: null });
    }

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    const monthYearList = Array.from({ length: 12 }, (_, i) => {
      const d = new Date(currentYear, i, 1);
      const monthShort = new Intl.DateTimeFormat("en", {
        month: "short",
      }).format(d);
      return `${monthShort} ${currentYear}`;
    });

    const currentMonthShort = new Intl.DateTimeFormat("en", {
      month: "short",
    }).format(currentDate);
    const currentMonthYear = `${currentMonthShort} ${currentYear}`;

    setMonthYearOptions(monthYearList);
    setSelectedMonthYear((prev) => prev || currentMonthYear);

    axios
      .get(`${BASE_URL}/api/states`, { headers: getAuthHeaders() })
      .then((res) => setStates(res.data))
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!selectedState) return;

    axios
      .get(`${BASE_URL}/api/employees?state_id=${selectedState}`, {
        headers: getAuthHeaders(),
      })
      .then((res) => setEmployees(res.data))
      .catch(console.error);
  }, [selectedState]);

  // ✅ FIX: Clear leaveData whenever any filter changes so stale data from a
  // previous selection is never visible while a new fetch is in flight or
  // before the user clicks Go again.
  useEffect(() => {
    setLeaveData([]);
    setEditDate(null);
    setEditFormData({});
  }, [selectedState, selectedEmployee, selectedMonthYear]);

  const baseColumns = 8;
  const extraColumns =
    (!isExpenseUser ? 1 : 0) +
    (!isCoordinator ? 1 : 0) +
    (!isExpenseUser ? 3 : 0);

  const totalColumns = baseColumns + extraColumns;

  const handleGo = useCallback(() => {
    if (!selectedState || !selectedEmployee || !selectedMonthYear) {
      alert("Please select State, Employee, and Month/Year.");
      return;
    }

    const normalized = normalizeMonthYear(selectedMonthYear);
    const monthYearParam = encodeURIComponent(normalized);

    // ✅ FIX: Always clear before fetching so no old employee's rows are ever
    // mixed with or shown ahead of the incoming API response.
    setLeaveData([]);
    setIsLoading(true);

    axios
      .get(
        `${BASE_URL}/api/dcr-leave-assignments/filter?state_id=${selectedState}&employee_id=${selectedEmployee}&monthYear=${monthYearParam}`,
        { headers: getAuthHeaders() },
      )
      .then((res) => setLeaveData(res.data))
      .catch(() => alert("Failed to fetch leave data."))
      .finally(() => setIsLoading(false));
  }, [selectedState, selectedEmployee, selectedMonthYear]);

  const handleRowClick = (employeeId, date) => {
    const id = employeeId || selectedEmployee;
    if (!id) return alert("Please select an employee first");

    const dateStr = new Date(date).toISOString().split("T")[0];
    const stateToPersist = {
      fromLeaveReport: true,
      filters: {
        selectedState,
        selectedEmployee,
        selectedMonthYear,
      },
      leaveData,
      selectedEditDate: editDate,
      editFormData,
    };
    navigate(location.pathname, { replace: true, state: stateToPersist });
    const url = `/pobdatewisedetails?employeeId=${encodeURIComponent(
      id,
    )}&date=${encodeURIComponent(dateStr)}`;
    window.open(url, "_blank");
  };

  const handleEditClick = (date) => {
    setEditDate(date);
    const formData = {};
    leaveData
      .filter((row) => row.date === date)
      .forEach((row) => {
        formData[row.id] = {
          remarks: row.remarks || "",
          is_dcr_open: !!row.is_dcr_open,
          is_temptation_open: !!row.is_temptation_open,
          is_morning_punch_open: !!row.is_morning_punch_open,
          is_expense_open: !!row.is_expense_open,

          evening_punch_open: row.evening_punch_open || "",
          original_evening_punch_open: row.evening_punch_open || "",
        };
      });
    setEditFormData(formData);
  };

  const handleCancel = () => {
    setEditDate(null);
    setEditFormData({});
  };

  const handleCheckboxChange = (e, id) => {
    const { name, checked } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [name]: checked,
      },
    }));
  };

  const handleRemarksChange = (e, id) => {
    const value = e.target.value;
    setEditFormData((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        remarks: value,
      },
    }));
  };

  const handleUpdate = async () => {
    try {
      const rowsToUpdate = leaveData.filter((row) => row.date === editDate);

      for (const row of rowsToUpdate) {
        const formData = editFormData[row.id];
        if (!formData) continue;

        const employeeId = row.employee_id || Number(selectedEmployee);
        if (!employeeId) continue;

        const targetDateStr = row.date.slice(0, 10); // ✅ no timezone conversion

        let isMorningPunchOpen = !!formData.is_morning_punch_open;
        let isDcrOpen = !!formData.is_dcr_open;
        let isTemptationOpen = !!formData.is_temptation_open;
        let isExpenseOpen = !!formData.is_expense_open;

        if (targetDateStr === todayStr && isMorningPunchOpen) {
          isDcrOpen = true; // ✅ now actually triggers
          isTemptationOpen = true;
          isExpenseOpen = true;
        }

        const payload = {
          employee_id: employeeId,
          target_date: targetDateStr,
          is_dcr_open: isDcrOpen,
          is_temptation_open: isTemptationOpen,
          is_morning_punch_open: isMorningPunchOpen,
          is_expense_open: isExpenseOpen,
          remarks: formData.remarks || "",
        };
        if ("evening_punch_open" in formData) {
          const newValue = formData.evening_punch_open;
          if (!newValue || newValue.trim() === "") {
            payload.evening_punch_time = null;
          } else {
            payload.evening_punch_time = newValue;
          }
        }
        await axios.post(`${BASE_URL}/api/open-tour-flags`, payload, {
          headers: getAuthHeaders(),
        });
      }

      alert("Updated successfully!");
      handleCancel();
      handleGo();
    } catch (error) {
      alert("Failed to update data.");
    }
  };

  return (
    <div className="p-2  border-2 border-gray-300 rounded-lg bg-white">
      <div className="p-2  border-2 border-gray-300 rounded-lg bg-white">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700">Unlock </span>
          <span className="text-blue-500">DCR</span>
        </h1>

        <div className="flex justify-center items-center">
          <div className="w-full max-w-6xl">
            <div className="unlock-dcr-filter-grid grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-3">
                <label className="text-sm font-semibold whitespace-nowrap">
                  State
                </label>
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg text-sm"
                >
                  <option value="">Select State</option>
                  {states.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-3">
                <label className="text-sm font-semibold whitespace-nowrap">
                  Employee
                </label>
                <select
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg text-sm"
                >
                  <option value="">Select Employee</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}  | {emp.employee} | {emp.designation} |{" "}
                      {emp.headquarter_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-3">
                <label className="text-sm font-semibold whitespace-nowrap">
                  Month/Year:
                </label>
                <select
                  value={selectedMonthYear}
                  onChange={(e) => setSelectedMonthYear(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg text-sm"
                >
                  {monthYearOptions.map((my, idx) => (
                    <option key={idx} value={my}>
                      {my}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-center mt-6">
              <CustomButton variant="go" onClick={handleGo}>
                Go
              </CustomButton>
            </div>
          </div>
        </div>

        <div className="unlock-dcr-table-section mt-4">
          {isLoading && (
            <div className="text-center py-4">
              <Loader onCancel={() => setIsLoading(false)} />
            </div>
          )}
          <div className="border rounded shadow  table-wrapper">
            <table className="unlock-dcr-leave-table text-sm ">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th className="border p-2">S.No</th>
                  <th className="border p-2">Working Date</th>
                  <th className="border p-2">Leave</th>
                  <th className="border p-2">Updated By</th>
                  <th className="border p-2">Remarks (Why Opening)</th>
                  <th className="border p-2">Visits</th>
                  {!isExpenseUser && (
                    <>
                      <th className="border p-2">DCR Open</th>

                      <th className="border p-2">Daily Temptation Open</th>
                    </>
                  )}
                  {!isCoordinator && <th className="border p-2">Expenses</th>}
                  {!isExpenseUser && (
                    <>
                      <th className="border p-2">Morning Punch Open</th>
                      <th className="border p-2">Morning Punch Time</th>
                      <th className="border p-2">Evening Punch Time</th>
                    </>
                  )}
                  <th className="border p-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {leaveData.length > 0 ? (
                  leaveData.map((item, idx) => {
                    const isEditing = editDate === item.date;
                    const locked = isRowLocked(item);

                    return (
                      <tr
                        key={item.id}
                        className="hover:bg-gray-100"
style={{
  backgroundColor: (() => {
    const type = (item.leave_type_name || "").toLowerCase().trim();
    const dateObj = new Date(item.date.slice(0, 10) + "T00:00:00");
    const isSunday = dateObj.getDay() === 0;
    return type === "sunday" || type === "holiday" || isSunday ? "#5eead4" : "";
  })(),
}}
                      >
                        <td className="border p-2 text-center">{idx + 1}</td>
                       <td className="border p-2 cursor-pointer">
                      {formatDate(item.date)}{" "}
                      {new Date(item.date.slice(0, 10) + "T00:00:00").toLocaleString("en-GB", { weekday: "short" })}
                    </td>
                        <td className="border p-2 font-bold">
                          {item.leave_type_name || ""}
                        </td>

                        <td className="border p-2">{item.updated_by || ""}</td>

                        <td className="border p-2">
                          {isEditing ? (
                            <textarea
                              value={editFormData[item.id]?.remarks || ""}
                              onChange={(e) => handleRemarksChange(e, item.id)}
                              className="border rounded w-full p-1 text-sm"
                              rows={2}
                              disabled={locked}
                            />
                          ) : (
                            item.remarks || ""
                          )}
                        </td>

                        <td className="border p-2 text-center">
                          <a
                            href="#"
                            className="text-blue-500 underline"
                            onClick={(e) => {
                              e.preventDefault();
                              handleRowClick(item.employee_id, item.date);
                            }}
                          >
                            {item.visits}
                          </a>
                        </td>
                        {/* ✅ ALWAYS SHOW DCR */}
                        {!isExpenseUser && (
                          <td className="border p-2 text-center">
                            <input
                              type="checkbox"
                              name="is_dcr_open"
                              checked={
                                isEditing
                                  ? editFormData[item.id]?.is_dcr_open || false
                                  : !!item.is_dcr_open
                              }
                              disabled={!isEditing || locked}
                              onChange={(e) => handleCheckboxChange(e, item.id)}
                            />
                          </td>
                        )}
                        {/* ❌ HIDE ONLY TEMPTATION */}
                        {!isExpenseUser && (
                          <td className="border p-2 text-center">
                            <input
                              type="checkbox"
                              name="is_temptation_open"
                              checked={
                                isEditing
                                  ? editFormData[item.id]?.is_temptation_open ||
                                    false
                                  : !!item.is_temptation_open
                              }
                              disabled={!isEditing || locked}
                              onChange={(e) => handleCheckboxChange(e, item.id)}
                            />
                          </td>
                        )}

                        {!isCoordinator && (
                          <td className="border p-2 text-center">
                            <input
                              type="checkbox"
                              name="is_expense_open"
                              checked={
                                isEditing
                                  ? editFormData[item.id]?.is_expense_open ||
                                    false
                                  : !!item.is_expense_open
                              }
                              disabled={!isEditing || locked}
                              onChange={(e) => handleCheckboxChange(e, item.id)}
                            />
                          </td>
                        )}

                        {/* ❌ HIDE THESE ALSO */}
                        {!isExpenseUser && (
                          <>
                            <td className="border p-2 text-center">
                              <input
                                type="checkbox"
                                name="is_morning_punch_open"
                                checked={
                                  isEditing
                                    ? editFormData[item.id]
                                        ?.is_morning_punch_open || false
                                    : !!item.is_morning_punch_open
                                }
                                disabled={
                                  !isEditing ||
                                  !!item.is_morning_punch_open ||
                                  locked
                                }
                                onChange={(e) =>
                                  handleCheckboxChange(e, item.id)
                                }
                              />
                            </td>

                            <td className="border p-2 text-center">
                              {formatTimeTo12Hour(
                                item.morning_punch_open || "",
                              )}
                            </td>

                            <td className="border p-2 text-center">
                              {isEditing ? (
                                <div className="flex items-center justify-center gap-2">
                                  {editFormData[item.id]?.evening_punch_open ? (
                                    <>
                                      <span>
                                        {formatTimeTo12Hour(
                                          editFormData[item.id]
                                            .evening_punch_open,
                                        )}
                                      </span>
                                      <button
                                        onClick={() =>
                                          setEditFormData((prev) => ({
                                            ...prev,
                                            [item.id]: {
                                              ...prev[item.id],
                                              evening_punch_open: "",
                                            },
                                          }))
                                        }
                                        className="text-red-600 text-sm font-bold"
                                      >
                                        ❌
                                      </button>
                                    </>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </div>
                              ) : item.evening_punch_open &&
                                item.evening_punch_open !== "00:00:00" ? (
                                formatTimeTo12Hour(item.evening_punch_open)
                              ) : (
                                ""
                              )}
                            </td>
                          </>
                        )}
                        <td className="border p-2 text-center">
                          {isEditing ? (
                            <div className="flex flex-col gap-1 items-center">
                              <button
                                onClick={handleUpdate}
                                className="bg-green-500 text-white px-2 py-1 text-xs rounded w-20"
                                disabled={locked}
                              >
                                Update
                              </button>
                              <button
                                onClick={handleCancel}
                                className="bg-red-500 text-white px-2 py-1 text-xs rounded w-20"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleEditClick(item.date)}
                              className="bg-blue-500 text-white px-2 py-1 text-xs rounded"
                            >
                              Edit
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={totalColumns}>No data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaveReportPage;